/**
 * DEAD SIGNAL — Dialogue Panel
 *
 * Middle layer of the three-layer UI. Displays the dialogue transcript,
 * NPC dialogue, LUMEN readouts, stage directions, and system
 * messages with typewriter text rendering.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useGameStore } from '../state/gameState.js';
import { AudioSystem } from '../systems/AudioSystem.js';

// ── Typewriter Text Component ────────────────────────
function TypewriterText({ text, speed = 25, onComplete, className }) {
  const [displayed, setDisplayed] = useState('');
  const [isDone, setIsDone] = useState(false);
  const indexRef = useRef(0);

  useEffect(() => {
    setDisplayed('');
    setIsDone(false);
    indexRef.current = 0;

    if (!text) return;

    const interval = setInterval(() => {
      indexRef.current += 1;
      if (indexRef.current >= text.length) {
        setDisplayed(text);
        setIsDone(true);
        clearInterval(interval);
        onComplete?.();
      } else {
        setDisplayed(text.slice(0, indexRef.current));
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed, onComplete]);

  return (
    <span className={className}>
      {displayed}
      {!isDone && <span className="typewriter-cursor" />}
    </span>
  );
}

// ── Overwrite Animation Line ────────────────────────────
// Player's text types in, pauses 1.5s, erases right-to-left, then
// the fabricated compliance text retypes at normal speed.
function OverwriteLine({ line }) {
  const [phase, setPhase] = useState('typing');     // typing → showing → erasing → retyping → done
  const [displayed, setDisplayed] = useState('');
  const [showModified, setShowModified] = useState(false);
  const indexRef = useRef(0);

  const { original, fabricated } = line;

  useEffect(() => {
    // Phase 1: Type the original text
    const typeInterval = setInterval(() => {
      indexRef.current += 1;
      if (indexRef.current >= original.length) {
        setDisplayed(original);
        clearInterval(typeInterval);
        setPhase('showing');
      } else {
        setDisplayed(original.slice(0, indexRef.current));
      }
    }, 25);
    return () => clearInterval(typeInterval);
  }, [original]);

  useEffect(() => {
    if (phase !== 'showing') return;
    // Phase 2: Pause 1.5s so player reads their words
    const timer = setTimeout(() => {
      setPhase('erasing');
      indexRef.current = original.length;
    }, 1500);
    return () => clearTimeout(timer);
  }, [phase, original]);

  useEffect(() => {
    if (phase !== 'erasing') return;
    // Phase 3: Erase original right-to-left, fast (~50ms/char)
    const eraseInterval = setInterval(() => {
      indexRef.current -= 1;
      if (indexRef.current <= 0) {
        setDisplayed('');
        clearInterval(eraseInterval);
        setPhase('retyping');
        indexRef.current = 0;
      } else {
        setDisplayed(original.slice(0, indexRef.current));
      }
    }, 50);
    return () => clearInterval(eraseInterval);
  }, [phase, original]);

  useEffect(() => {
    if (phase !== 'retyping') return;
    // Phase 4: Retype fabricated text at normal speed
    const retypeInterval = setInterval(() => {
      indexRef.current += 1;
      if (indexRef.current >= fabricated.length) {
        setDisplayed(fabricated);
        clearInterval(retypeInterval);
        setPhase('done');
        setShowModified(true);
        // Clear the overwrite state
        useGameStore.getState().clearOverwrite();
      } else {
        setDisplayed(fabricated.slice(0, indexRef.current));
      }
    }, 25);
    return () => clearInterval(retypeInterval);
  }, [phase, fabricated]);

  const isErasing = phase === 'erasing';
  const isRetyping = phase === 'retyping' || phase === 'done';

  return (
    <div className={`dialogue-line player ${isErasing ? 'overwrite-erasing' : ''} ${isRetyping ? 'overwrite-fabricated' : ''}`}>
      <strong>LUMEN ▸ </strong>
      <span>{displayed}</span>
      {(phase === 'typing' || phase === 'erasing' || phase === 'retyping') && <span className="typewriter-cursor" />}
      {showModified && <span className="overwrite-tag"> [MODIFIED]</span>}
    </div>
  );
}

// ── Single Dialogue Line ─────────────────────────────
function DialogueLine({ line, isLatest }) {
  // Handle overwrite animation
  if (line.type === 'overwrite') {
    return <OverwriteLine line={line} />;
  }
  const typeClass = `dialogue-line ${line.type || 'npc'}`;

  // Only typewrite the latest line
  if (isLatest && !line.instant) {
    return (
      <div className={typeClass}>
        {line.speaker && <strong>{line.speaker}: </strong>}
        <TypewriterText text={line.text} speed={line.type === 'lumen' ? 15 : 25} />
      </div>
    );
  }

  return (
    <div className={typeClass}>
      {line.speaker && <strong>{line.speaker}: </strong>}
      {line.text}
    </div>
  );
}

// ── Thinking Indicator ──────────────────────────────────
function ThinkingIndicator() {
  const interviewActive = useGameStore((s) => s.interview.active);
  const speaker = interviewActive ? 'HARGROVE' : 'CRAINE';
  return (
    <div className="dialogue-line thinking-indicator">
      <strong>{speaker}: </strong>
      <span className="thinking-dots">
        <span className="thinking-dot" />
        <span className="thinking-dot" />
        <span className="thinking-dot" />
      </span>
    </div>
  );
}

// ── Main Dialogue Panel ──────────────────────────────
export default function DialoguePanel() {
  const dialogueLines = useGameStore((s) => s.dialogueLines);
  const isProcessing = useGameStore((s) => s.isProcessing);
  const streamingLine = useGameStore((s) => s.streamingLine);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom on new dialogue and typewriter updates
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollToBottom = () => {
      container.scrollTop = container.scrollHeight;
    };

    // Scroll immediately
    scrollToBottom();

    // Observe child additions/text mutations (e.g. typewriter additions) to scroll smoothly
    const observer = new MutationObserver(scrollToBottom);
    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Schedule frame rendering to capture final container dimensions
    const handle = requestAnimationFrame(scrollToBottom);

    // Audio effects for new incoming lines
    if (dialogueLines.length > 0) {
      const latest = dialogueLines[dialogueLines.length - 1];
      // The LUMEN system-alert chime is for data readouts/diagnostics — not for
      // narrative LUMEN guidance (e.g. the office-arrival "He's expecting us"
      // line), which can opt out with `noChime` so it doesn't stack a spurious
      // "beep-beep" on top of the elevator ding when the scene loads.
      if (latest.type === 'lumen' && !latest.text.startsWith('> ') && !latest.noChime) {
        AudioSystem.playSystemAlert();
      }
    }

    return () => {
      observer.disconnect();
      cancelAnimationFrame(handle);
    };
  }, [dialogueLines, isProcessing, streamingLine]);

  return (
    <div className="dialogue-panel">

      {/* Text area */}
      <div className="dialogue-text-area" ref={scrollRef}>
        {dialogueLines.length === 0 && (
          <div className="dialogue-line lumen" style={{ opacity: 0.4 }}>
            {'> LUMEN COGNITIVE ENHANCEMENT SYSTEM — ONLINE'}
          </div>
        )}
        {dialogueLines.map((line, i) => (
          <DialogueLine
            key={`${i}-${line.text?.slice(0, 20)}`}
            line={line}
            isLatest={i === dialogueLines.length - 1}
          />
        ))}
        {/* Provisional streaming line: the model's dialogue text arriving live.
            Replaces the thinking indicator while tokens flow; the finalized
            line (rendered instantly from dialogueLines) takes over when the
            full response parses. */}
        {streamingLine && (
          <div className="dialogue-line npc">
            {streamingLine.speaker && <strong>{streamingLine.speaker}: </strong>}
            <span>
              {streamingLine.text}
              <span className="typewriter-cursor" />
            </span>
          </div>
        )}
        {isProcessing && !streamingLine && <ThinkingIndicator />}
      </div>
    </div>
  );
}
